import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAgentSpecResourceModel } from "@/components/studio/AgentSpecResourceWorkspace";
import { AGENT_SPEC_PULL_QUERY_KEY } from "@/hooks/invalidateAgentSpecResources";
import { agentSpecsAPI } from "@/services/api/agentSpecs";
import { studioThreadListedOnHost } from "@/components/studio/studioThreadUtils";
import { studioThreadsQueryKey } from "@/hooks/useAgentSpecStudioThreads";
import { useAgentSpecStudioStore } from "@/store/agentSpecStudio";
import { findContextDeployment, useContextStore } from "@/store/context";

/** Selected fil if it belongs to this Agent — never another Agent's leftover. */
export function treeDraftThreadIdForAgent(
  _agentId: string,
  _currentAgentId: string | null | undefined,
  selectedThreadId: string | null | undefined,
  hostThreads?: ReadonlyArray<{ id: string }>,
): string | null {
  if (!hostThreads) return null;
  if (studioThreadListedOnHost(selectedThreadId, hostThreads)) {
    return selectedThreadId!.trim();
  }
  return null;
}

/** Prefer the selected host fil, else a fil that carries Workflows/MiniApps. */
export function treeNavigationThreadId(
  selectedOnHost: string | null,
  hostThreads: ReadonlyArray<{ id: string }>,
  resourceThreadIds: ReadonlyArray<string>,
): string | null {
  if (selectedOnHost) return selectedOnHost;
  const withResources = hostThreads.find((thread) =>
    resourceThreadIds.includes(thread.id),
  );
  return withResources?.id ?? hostThreads[0]?.id ?? null;
}

export function useAgentSpecTreeResources(
  organizationId: string | null | undefined,
  agentId: string,
  enabled: boolean,
) {
  const environmentName = useContextStore(
    (state) => findContextDeployment(state, agentId)?.environment ?? state.env,
  );
  const currentAgentId = useContextStore((state) => state.deploymentId);
  const selectedThreadId = useAgentSpecStudioStore(
    (state) => state.selectedThreadId,
  );
  const org = enabled ? (organizationId ?? null) : null;
  const scopedAgent = enabled ? agentId : null;
  const threadsQuery = useQuery({
    queryKey: studioThreadsQueryKey(organizationId, agentId),
    queryFn: () => agentSpecsAPI.listStudioThreads(organizationId!, agentId),
    enabled: Boolean(enabled && organizationId && agentId),
    retry: false,
    staleTime: 60_000,
  });
  const hostThreads = threadsQuery.isSuccess
    ? (threadsQuery.data?.threads ?? [])
    : [];
  const selectedOnHost = enabled
    ? treeDraftThreadIdForAgent(
        agentId,
        currentAgentId,
        selectedThreadId,
        threadsQuery.isSuccess ? hostThreads : undefined,
      )
    : null;
  const pullQuery = useQuery({
    queryKey: [AGENT_SPEC_PULL_QUERY_KEY, organizationId, agentId, environmentName],
    queryFn: () =>
      agentSpecsAPI.pull(organizationId!, agentId, {
        environmentName: environmentName ?? undefined,
      }),
    enabled: Boolean(enabled && organizationId && agentId),
    retry: false,
    staleTime: 60_000,
  });
  const model = useAgentSpecResourceModel(
    org,
    scopedAgent,
    selectedOnHost,
    pullQuery.data ?? null,
  );
  const navigationThreadId = treeNavigationThreadId(
    selectedOnHost,
    hostThreads,
    [],
  );

  return useMemo(
    () => ({
      threadId: navigationThreadId,
      workflows: model.workflows.map((item) => ({
        id: item.id,
        title: item.title,
      })),
      miniapps: model.miniapps.map((item) => ({
        id: item.id,
        title: item.title,
      })),
    }),
    [model.miniapps, model.workflows, navigationThreadId],
  );
}
